<template>
  <div class="page-view">

    <BasePageHeader :title="customer?.name || $t('entity.customer')" @back="$router.back()">
      <template #actions>
        <button v-if="!editing" class="btn ghost sm" @click="startEdit">{{ $t('common.edit') }}</button>
        <template v-else>
          <button class="btn ghost sm" @click="cancelEdit">{{ $t('common.cancel') }}</button>
          <button class="btn primary sm" :disabled="saving || !form.name" @click="save">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </template>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />

    <template v-else-if="customer">

      <!-- Info card -->
      <div class="mx-4 my-4 bg-[var(--color-surface)] border border-gray-800 rounded-xl overflow-hidden">
        <template v-if="!editing">
          <div class="flex items-baseline gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('common.name') }}</span>
            <span class="flex-1 text-sm text-gray-200">{{ customer.name }}</span>
          </div>
          <div v-if="customer.email" class="flex items-baseline gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('customers.email') }}</span>
            <a :href="`mailto:${customer.email}`" class="flex-1 text-sm text-blue-400 no-underline hover:underline">{{ customer.email }}</a>
          </div>
          <div v-if="customer.phone" class="flex items-baseline gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('customers.phone') }}</span>
            <a :href="`tel:${customer.phone}`" class="flex-1 text-sm text-blue-400 no-underline hover:underline">{{ customer.phone }}</a>
          </div>
          <div v-if="customer.address" class="flex items-baseline gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('customers.address') }}</span>
            <span class="flex-1 text-sm text-gray-200">{{ customer.address }}</span>
          </div>
          <div v-if="customer.contact_person" class="flex items-baseline gap-3 px-4 py-3 border-b border-gray-800 last:border-b-0">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('customers.contactPerson') }}</span>
            <span class="flex-1 text-sm text-gray-200">{{ customer.contact_person }}</span>
          </div>
          <div class="flex items-baseline gap-3 px-4 py-3">
            <span class="w-20 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide">{{ $t('customers.priceGroup') }}</span>
            <span class="flex-1 text-sm text-gray-200">
              <span v-if="customer.price_group">
                {{ priceGroups.find(pg => pg.id === customer.price_group)?.name || `#${customer.price_group}` }}
              </span>
              <span v-else class="text-gray-600">{{ $t('common.none') }}</span>
            </span>
          </div>
        </template>
        <template v-else>
          <div class="px-4 py-2.5 border-b border-gray-800 last:border-b-0">
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="form.name" placeholder="Ola Nordmann" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5 border-b border-gray-800 last:border-b-0">
            <BaseFormField :label="$t('customers.email')">
              <BaseInput v-model="form.email" placeholder="ola@eksempel.no" type="email" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5 border-b border-gray-800 last:border-b-0">
            <BaseFormField :label="$t('customers.phone')">
              <BaseInput v-model="form.phone" placeholder="+47 000 00 000" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5 border-b border-gray-800 last:border-b-0">
            <BaseFormField :label="$t('customers.address')">
              <BaseInput v-model="form.address" placeholder="Storgata 1, 0182 Oslo" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5 border-b border-gray-800 last:border-b-0">
            <BaseFormField :label="$t('customers.contactPerson')">
              <BaseInput v-model="form.contact_person" placeholder="Kari Nordmann" />
            </BaseFormField>
          </div>
          <div class="px-4 py-2.5">
            <BaseFormField :label="$t('customers.priceGroup')">
              <BaseSelect v-model="form.price_group">
                <option :value="null">{{ $t('common.none') }}</option>
                <option v-for="pg in priceGroups" :key="pg.id" :value="pg.id">{{ pg.name }}</option>
              </BaseSelect>
            </BaseFormField>
          </div>
        </template>
      </div>

      <!-- Projects section -->
      <div class="card m-4 p-5">
        <div class="flex items-center mb-2">
          <div class="section-label flex-1">{{ $t('entity.project_plural') }}</div>
          <button class="btn primary sm" @click="newProject">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {{ $t('common.new') }}
          </button>
        </div>

        <EmptyState v-if="projectsLoading" :message="$t('customers.loadingProjects')" />
        <EmptyState v-else-if="!projects.length" :message="$t('customers.noProjects')" />
        <div v-else class="list-stack">
          <div
            v-for="project in projects"
            :key="project.id"
            class="data-card cursor-pointer"
            @click="$router.push(`/project/${project.id}`)"
          >
            <div class="data-card__main">
              <div class="data-card__name">{{ project.name }}</div>
              <div class="data-card__meta">
                <span v-if="project.start_time" class="badge" style="background:#1e3a5f;color:#93c5fd">
                  {{ formatDate(project.start_time) }} – {{ formatDate(project.end_time) }}
                </span>
                <span v-if="project.location" class="badge" style="background:#2d2a1e;color:#fcd34d">
                  {{ project.location }}
                </span>
                <span v-if="project.status != null" class="badge" :style="statusStyle(project.status)">
                  {{ project.status_display }}
                </span>
              </div>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-gray-500 shrink-0">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </div>
      </div>

    </template>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getCustomers, putCustomer, getProjects, getPriceGroups } from '@/api/api'
import { useToast } from '@/composables/useToast'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'

const route  = useRoute()
const router = useRouter()
const toast  = useToast()
const { t }  = useI18n()

const customerId = route.params.customerId

const customer      = ref(null)
const loading       = ref(false)
const loadError     = ref(null)
const editing       = ref(false)
const saving        = ref(false)
const form          = ref({ name: '', email: '', phone: '', address: '', contact_person: '', price_group: null })
const projects      = ref([])
const projectsLoading = ref(false)
const priceGroups   = ref([])

const STATUS_STYLES = {
  0: { background: '#1f2937', color: '#9ca3af' },
  1: { background: '#1e3a5f', color: '#93c5fd' },
  2: { background: '#0f3030', color: '#5eead4' },
  3: { background: '#2d2010', color: '#fbbf24' },
  4: { background: '#2d2500', color: '#fde68a' },
  5: { background: '#0d2d1a', color: '#4ade80' },
  6: { background: '#2d1a00', color: '#fb923c' },
  7: { background: '#0d2020', color: '#34d399' },
  8: { background: '#2d1010', color: '#f87171' },
}

function statusStyle(status) {
  return STATUS_STYLES[status] ?? {}
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function startEdit() {
  form.value = {
    name:           customer.value.name ?? '',
    email:          customer.value.email ?? '',
    phone:          customer.value.phone ?? '',
    address:        customer.value.address ?? '',
    contact_person: customer.value.contact_person ?? '',
    price_group:    customer.value.price_group ?? null,
  }
  editing.value = true
}

function cancelEdit() {
  editing.value = false
}

async function save() {
  saving.value = true
  try {
    const updated = await putCustomer({ id: customerId, ...form.value })
    customer.value = updated
    editing.value  = false
    toast.success(t('customers.updated'))
  } catch {
    toast.error(t('customers.saveFailed'))
  } finally {
    saving.value = false
  }
}

function newProject() {
  router.push({ path: '/project/new', query: { customer: customerId } })
}

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    customer.value = await getCustomers(customerId)
  } catch {
    loadError.value = t('customers.loadCustomerFailed')
  } finally {
    loading.value = false
  }
}

async function loadProjects() {
  projectsLoading.value = true
  try {
    const res = await getProjects({ customer: customerId, page_size: 100 })
    projects.value = res.results ?? res ?? []
  } catch {
    // Silently fail — not critical
  } finally {
    projectsLoading.value = false
  }
}

onMounted(async () => {
  await load()
  loadProjects()
  getPriceGroups().then(res => { priceGroups.value = res.results ?? res ?? [] }).catch(() => {})
})
</script>


